import { Breadcrumbs, Link as LinkMUI } from "@mui/material";
import { FC, useMemo } from "react";
import { BREADCRUMB_PAGE } from "./constants";
import { ChevronLeft } from "@mui/icons-material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AdminRoutes, CoachRoutes } from "config/routes";
import { useClinicText } from "hooks/useClinicText";

/** True when this tab already navigated inside the app. A new tab starts at 0. */
function hasInAppHistory(): boolean {
  const index = window.history.state?.idx;
  return typeof index === "number" && index > 0;
}

/** Derive a friendly "last page" label from document.referrer (pathname). */
function getBackLabelFromReferrer(): string {
  if (typeof document === "undefined" || !document.referrer) return "Go Back";
  try {
    const url = new URL(document.referrer);
    const path = url.pathname.replace(/\/$/, "") || "/";
    if (path === "/home") return "Home";
    if (path === "/feedback-portal") return "Feedback";
    if (path === "/feedbacks" || path === "/feedbacks/") return "Feedback";
    if (path === "/learners") return "Learners";
    if (path === "/requirements") return "Requirements";
    if (/^\/organization\/[^/]+\/learners\/?$/.test(path)) return "Learners";
    if (/^\/organization\/[^/]+\/feedbacks\/?$/.test(path)) return "Feedback";
    if (/^\/organization\/[^/]+\/all-feedback\/[^/]+$/.test(path)) return "All feedback";
    if (/^\/organization\/[^/]+\/learner\/[^/]+$/.test(path)) return "Learner details";
    if (/^\/learner\/[^/]+$/.test(path)) return "Learner details";
    if (/^\/organization\/[^/]+\/epas\/?$/.test(path)) return "EPAs";
    if (/^\/organization\/[^/]+\/competencies\/?$/.test(path)) return "Competencies";
    if (/^\/organization\/[^/]+\/graduation-class\/?$/.test(path)) return "Graduation class";
    if (/^\/organization\/[^/]+\/yearly-curriculum\/?$/.test(path)) return "Yearly curriculum";
    if (/^\/organization\/[^/]+\/wellness-resources\/?$/.test(path)) return "Wellness resources";
    if (/^\/organizations?\/?$/.test(path)) return "Organizations";
    return "Go Back";
  } catch {
    return "Go Back";
  }
}

type BreadcrumbItem =
  | { label: string; link: string; icon: React.ReactNode; useBack?: false }
  | { label: string; icon: React.ReactNode; useBack: true };

type BreadcrumbProps = {
  page: string;
};

export const Breadcrumb: FC<BreadcrumbProps> = ({ page }) => {
  const params = useParams();
  const navigate = useNavigate();
  const { textYearlyCurriculum, textGraduationClasses } = useClinicText();
  const backLabel = useMemo(() => getBackLabelFromReferrer(), []);
  const canGoBack = hasInAppHistory();

  const breadcrumbList = useMemo((): BreadcrumbItem[] => {
    switch (page) {
      case BREADCRUMB_PAGE.ONBOARDING:
      case BREADCRUMB_PAGE.FEEDBACK_GUIDE:
      case BREADCRUMB_PAGE.HOME:
        return [{ label: "Home", link: "/home", icon: <ChevronLeft /> }];
      case BREADCRUMB_PAGE.EPA_DETAIL:
        return [{ label: "EPAs", link: "/epas", icon: <ChevronLeft /> }];
      case BREADCRUMB_PAGE.COMPETENCY_DETAIL:
        return [
          {
            label: "Competencies",
            link: "/competencies",
            icon: <ChevronLeft />,
          },
        ];
      case BREADCRUMB_PAGE.ACADEMIC_YEAR:
        return [
          {
            label: "Programs",
            link: "/academic-year",
            icon: <ChevronLeft />,
          },
        ];
      case BREADCRUMB_PAGE.GRADUATION_CLASS:
        return [
          {
            label: textGraduationClasses,
            link: `/organization/${params.organizationId}/graduation-class/`,
            icon: <ChevronLeft />,
          },
        ];
      case BREADCRUMB_PAGE.YEARLY_CURRICULUM:
        return [
          {
            label: textYearlyCurriculum,
            link: `/organization/${params.organizationId}/yearly-curriculum/`,
            icon: <ChevronLeft />,
          },
        ];
      case BREADCRUMB_PAGE.ORGANIZATION:
        return [
          {
            label: "Organizations",
            link: `/organizations`,
            icon: <ChevronLeft />,
          },
        ];
      case BREADCRUMB_PAGE.SITES:
        return [{ label: "Sites", link: "/sites", icon: <ChevronLeft /> }];
      case BREADCRUMB_PAGE.REQUIREMENT_PORTAL:
      case BREADCRUMB_PAGE.FEEDBACK_PORTAL:
      case BREADCRUMB_PAGE.FEEDBACK_ORGANIZATION:
      case BREADCRUMB_PAGE.FEEDBACK:
      case BREADCRUMB_PAGE.BACK:
        return canGoBack
          ? [
              {
                label: backLabel,
                icon: <ChevronLeft />,
                useBack: true,
              },
            ]
          : [
              {
                label: "Home",
                link: AdminRoutes.Home,
                icon: <ChevronLeft />,
              },
            ];
      case BREADCRUMB_PAGE.WELLNESS_RESOURCE:
        return [
          {
            label: "Wellness Resources",
            link: `/organization/${params.organizationId}/wellness-resources/`,
            icon: <ChevronLeft />,
          },
        ];
      case BREADCRUMB_PAGE.RESOURCE:
        return [
          {
            label: "Resources",
            link: "/resources",
            icon: <ChevronLeft />,
          },
        ];
      case BREADCRUMB_PAGE.USER:
        return [
          {
            label: "Users",
            link: "/users",
            icon: <ChevronLeft />,
          },
        ];
      case BREADCRUMB_PAGE.ORGANIZATION_REQUIREMENT_EPAS:
        return [
          {
            label: "Epas",
            link: `/organization/${params.organizationId}/epas/`,
            icon: <ChevronLeft />,
          },
        ];
      case BREADCRUMB_PAGE.ORGANIZATION_REQUIREMENT_COMPETENCIES:
        return [
          {
            label: "Competencies",
            link: `/organization/${params.organizationId}/competencies/`,
            icon: <ChevronLeft />,
          },
        ];
      case BREADCRUMB_PAGE.ORGANIZATION_REQUIREMENT_ITERS:
        return [
          {
            label: "ITERs",
            link: `/organization/${params.organizationId}/iters`,
            icon: <ChevronLeft />,
          },
        ];
      case BREADCRUMB_PAGE.ITER_LIST:
        return [{ label: "ITERs", link: "/iters", icon: <ChevronLeft /> }];
      case BREADCRUMB_PAGE.SURVEY_CREATE_AND_EDIT:
        return [{ label: "Surveys", link: "/surveys", icon: <ChevronLeft /> }];
      case BREADCRUMB_PAGE.ORGANIZATION_SURVEY_CREATE_AND_EDIT:
        return [
          {
            label: "Surveys",
            link: `/organization/${params.organizationId}/surveys`,
            icon: <ChevronLeft />,
          },
        ];
      case BREADCRUMB_PAGE.ORGANIZATION_LEARNERS:
        return [
          {
            label: "Learners",
            link: `/organization/${params.organizationId}/learners/`,
            icon: <ChevronLeft />,
          },
        ];
      case BREADCRUMB_PAGE.LEARNERS:
        return [
          {
            label: "Learners",
            link: CoachRoutes.Learners,
            icon: <ChevronLeft />,
          },
        ];
      default:
        return [];
    }
  }, [
    page,
    params.organizationId,
    backLabel,
    canGoBack,
    textGraduationClasses,
    textYearlyCurriculum,
  ]);

  if (breadcrumbList.length === 0) return null;

  return (
    <Breadcrumbs aria-label="breadcrumb">
      {breadcrumbList.map((element) =>
        element.useBack ? (
          <LinkMUI
            component="button"
            key={element.label}
            underline="hover"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ display: "flex", alignItems: "center", cursor: "pointer", background: "none", border: "none", font: "inherit", padding: 0 }}
          >
            {element.icon}
            {element.label}
          </LinkMUI>
        ) : (
          <LinkMUI
            component={Link}
            key={element.label}
            underline="hover"
            color="inherit"
            to={element.link}
            sx={{ display: "flex", alignItems: "center" }}
          >
            {element.icon}
            {element.label}
          </LinkMUI>
        ),
      )}
    </Breadcrumbs>
  );
};
